import { useState, useEffect } from "react";
import {
  RegularizationStatus,
  getRegularizationStatusLabel,
  getRegularizationStatusClass,
} from "../../constants/constants";
import { employeeDetailCache, fetchEmployeeFull } from "../../utils/employeeCache";
import { getAttendance } from "../services/attendanceservice";
import {
  formatDepartmentOf,
  formatDesignationOf,
  matchAttendanceForRegularization,
  getResolvedRegEmployee,
  setResolvedRegEmployee,
} from "./attendance";

// Proposed Check-In/Check-Out come back as plain "HH:MM:SS[.ffffff]"
// wall-clock time (no timezone/date component to shift, same as
// Attendance's check_in_time/check_out_time) — but unlike Attendance this
// value is never passed through a formatter, so it was rendering raw
// 24-hour strings, microseconds and all (e.g. "10:09:07.873569"). This
// reformats the digits into 12-hour + AM/PM, dropping seconds/microseconds,
// matching how Check-In/Check-Out already look everywhere else (Attendance
// log table, ViewAttendance).
const formatTime12 = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (!match) return value;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  if (hours === 0) hours = 12;

  return `${hours}:${minutes} ${period}`;
};

// Same idea as ViewLeave's skeleton: shown only for department/designation
// while their one-time fetch is in flight and nothing is known yet — "—"
// would read as "no data", this reads as "still loading". Emp ID/Name
// never need this since they always have fallback data available
// immediately.
const FieldSkeleton = () => (
  <span
    style={{
      display: "inline-block",
      width: 90,
      height: 14,
      borderRadius: 4,
      background: "var(--border-color, #e2e8f0)",
      opacity: 0.6,
      animation: "viewreg-pulse 1.1s ease-in-out infinite",
    }}
  />
);

/**
 * Regularization-detail popup. Mirrors ViewLeave (same popup shell, tabbed
 * layout, profile-grid/profile-card cards, employee-detail cache/fetch for
 * Department/Designation, footer action buttons) so the two "request"
 * modules in Attendance look and behave the same way.
 *
 * Employee resolution, in order of preference:
 *  1. The shared prefetch cache (see utils/regularizationEmployeeCache) —
 *     populated in the background by Attendance.jsx right after the
 *     regularization list loads, covering nearly every row before the user
 *     ever opens a popup. This is what removes the on-open lag.
 *  2. Data already embedded on the record itself (employee_detail) or on
 *     the matched `employees`/`attendanceRecords` rows passed in as props.
 *  3. An on-demand unscoped attendance search + full-employee fetch, as a
 *     safety net for the rare row the prefetch hasn't gotten to yet (e.g.
 *     opened in the brief window right after the list loads).
 *
 * Props:
 *  - regularization:    the regularization request row being viewed
 *  - employees:         flat employee list, used as a fallback match when the
 *                        API's embedded employee_detail is thin
 *  - attendanceRecords:  OPTIONAL — the attendance rows already loaded on the
 *                        Attendance page. Safe to omit — falls back to "—"
 *                        as before if not passed.
 *  - canManage:         whether Approve/Reject should show (approve/reject perms)
 *  - onClose:            close the popup
 *  - onApprovalAction:   (regId, action) => void — parent owns the confirm modal + API call
 */
const ViewRegularization = ({
  regularization,
  employees,
  attendanceRecords,
  canManage,
  onClose,
  onApprovalAction,
}) => {
  const r = regularization || {};
  const [viewTab, setViewTab] = useState("personal");

  const empDetail = r.employee_detail || {};

  // Matched independently of empId (by id, emp_id, or name) so it can
  // itself act as a fallback source for the employee id below.
  const matchedEmployee = (employees || []).find((employee) => {
    const targetId = r.employee ?? r.employee_id ?? empDetail.id;
    if (targetId && employee.id === targetId) return true;
    if (empDetail.emp_id && employee.emp_id === empDetail.emp_id) return true;

    const targetName = r.employee_name || empDetail.full_name;
    if (targetName) {
      return (
        (employee.full_name || employee.name || "").trim().toLowerCase() ===
        targetName.trim().toLowerCase()
      );
    }
    return false;
  });

  // Reuses the same matcher the Attendance-page prefetch uses, so the
  // popup's own fallback path never disagrees with what the prefetch
  // already decided.
  const matchedAttendanceDetail = matchAttendanceForRegularization(attendanceRecords, r);

  // Prefetch cache first — populated by Attendance.jsx in the background
  // right after the regularization list loads. When present, this alone
  // is enough to render instantly with no network wait.
  const cachedDetail = getResolvedRegEmployee(r.id);

  const directEmpId =
    r.employee ??
    r.employee_id ??
    empDetail.id ??
    cachedDetail?.id ??
    matchedEmployee?.id ??
    matchedAttendanceDetail?.id;

  const searchName = r.employee_name || empDetail.full_name || matchedEmployee?.full_name;
  const searchEmpId = empDetail.emp_id || r.emp_id || matchedEmployee?.emp_id;

  // What we already know about Department/Designation without any network
  // call — prefetch cache, embedded record data, or the matched
  // employees/attendance rows already passed in as props. When both are
  // already present, there's nothing left to fetch and the on-demand
  // search + full-employee fetch below are both skipped entirely.
  const baseDept =
    formatDepartmentOf(cachedDetail) ||
    formatDepartmentOf(empDetail) ||
    formatDepartmentOf(matchedEmployee) ||
    formatDepartmentOf(matchedAttendanceDetail) ||
    formatDepartmentOf(r) ||
    null;
  const baseDesig =
    formatDesignationOf(cachedDetail) ||
    formatDesignationOf(empDetail) ||
    formatDesignationOf(matchedEmployee) ||
    formatDesignationOf(matchedAttendanceDetail) ||
    formatDesignationOf(r) ||
    null;
  const baseComplete = !!(baseDept && baseDesig);

  // Safety-net search: only runs when the prefetch cache missed this row
  // AND nothing else resolved Department/Designation already. Runs an
  // unscoped attendance search (no date/status filter) purely to recover a
  // real employee id. Also writes its result into the shared cache, so if
  // this same row is ever viewed again, even that lookup is skipped too.
  const [searchedDetail, setSearchedDetail] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (baseComplete || directEmpId) {
      setSearchedDetail(null);
      setSearchLoading(false);
      return;
    }
    const query = searchEmpId || searchName;
    if (!query) {
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    getAttendance({ search: query, page_size: 5 })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || res;
        const rows = Array.isArray(data) ? data : data.results || [];
        const hit =
          rows.find((row) => {
            const detail = row.employee_detail || {};
            if (searchEmpId && (detail.emp_id === searchEmpId || row.emp_id === searchEmpId)) return true;
            if (searchName) {
              const rowName = detail.full_name || row.employee_name;
              return rowName && rowName.trim().toLowerCase() === searchName.trim().toLowerCase();
            }
            return false;
          }) || rows[0] || null;

        const resolved = hit ? hit.employee_detail || hit : null;
        setSearchedDetail(resolved);
        if (resolved) setResolvedRegEmployee(r.id, resolved);
      })
      .catch(() => {
        if (!cancelled) setSearchedDetail(null);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseComplete, directEmpId, searchEmpId, searchName]);

  const empId = directEmpId ?? searchedDetail?.id;

  const afterSearchComplete =
    baseComplete || !!((baseDept || formatDepartmentOf(searchedDetail)) && (baseDesig || formatDesignationOf(searchedDetail)));

  // Cache is shared with wherever the regularization/attendance list is
  // prefetched (same pattern as Leaves.jsx). If nothing was prefetched,
  // the effect below fetches it directly the first time this popup opens.
  const [viewEmpFull, setViewEmpFull] = useState(
    () => (empId ? employeeDetailCache.get(empId) : null) || null
  );
  // Only "loading" if we don't already have a cached full record.
  const [empLoading, setEmpLoading] = useState(() => !!empId && !employeeDetailCache.has(empId) && !afterSearchComplete);

  // Falls back to fetching directly only if a prefetch elsewhere hasn't
  // completed yet. fetchEmployeeFull dedupes against any prefetch already
  // in flight, so this never double-fetches. Skipped entirely once
  // afterSearchComplete is true — Department/Designation are already known
  // from data in hand, so there's nothing this fetch would add for those
  // fields, and skipping it removes a full extra round trip.
  useEffect(() => {
    if (!empId || afterSearchComplete) {
      setViewEmpFull(null);
      setEmpLoading(false);
      return;
    }

    const cached = employeeDetailCache.get(empId);
    if (cached) {
      setViewEmpFull(cached);
      setEmpLoading(false);
      return;
    }

    let cancelled = false;
    setEmpLoading(true);
    fetchEmployeeFull(empId)
      .then((full) => {
        if (!cancelled) {
          setViewEmpFull(full);
          setEmpLoading(false);
          if (full) setResolvedRegEmployee(r.id, full);
        }
      })
      .catch(() => {
        // Fetch failed (network error, bad id, etc.) — stop showing the
        // skeleton and fall through to the "—" / other fallbacks instead
        // of spinning forever.
        if (!cancelled) {
          setViewEmpFull(null);
          setEmpLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [empId, afterSearchComplete,r.id]);

  const stillResolving = searchLoading || empLoading;

  // Emp ID / Name have real fallback data (prefetch cache / employee_detail
  // / matched dropdown entry / matched attendance row / the searched
  // attendance row / the regularization record itself), so these render
  // correctly on the very first paint — no skeleton needed.
  const viewEmpId =
    viewEmpFull?.emp_id ||
    cachedDetail?.emp_id ||
    empDetail.emp_id ||
    matchedEmployee?.emp_id ||
    matchedAttendanceDetail?.emp_id ||
    searchedDetail?.emp_id ||
    r.emp_id ||
    "—";

  const viewName =
    viewEmpFull?.full_name ||
    cachedDetail?.full_name ||
    empDetail.full_name ||
    matchedEmployee?.full_name ||
    matchedEmployee?.name ||
    matchedAttendanceDetail?.full_name ||
    searchedDetail?.full_name ||
    r.employee_name ||
    "—";

  const deptKnown =
    formatDepartmentOf(viewEmpFull) ||
    baseDept ||
    formatDepartmentOf(searchedDetail);
  const desigKnown =
    formatDesignationOf(viewEmpFull) ||
    baseDesig ||
    formatDesignationOf(searchedDetail);

  const viewDept = deptKnown || (stillResolving ? null : "—");
  const viewDesig = desigKnown || (stillResolving ? null : "—");

  if (!regularization) return null;

  return (
    <div className="popup-overlay">
      <style>{`
        @keyframes viewreg-pulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.75; }
        }
      `}</style>
      <div
        className="popup-content premium-profile sticky-shell"
        style={{ maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* STICKY HEADER */}
        <div className="page-header" style={{ marginBottom: 6 }}>
          <h2 style={{ fontSize: 18 }}>Regularization Request Details</h2>
        </div>

        <hr className="premium-divider" />

        <div className="profile-tabs">
          <button
            className={viewTab === "personal" ? "active" : ""}
            onClick={() => setViewTab("personal")}
          >
            Employee Information
          </button>
          <button
            className={viewTab === "regularization" ? "active" : ""}
            onClick={() => setViewTab("regularization")}
          >
            Regularization Details
          </button>
        </div>

        {/* SCROLLABLE BODY */}
        <div className="popup-scroll-body">
          {/* EMPLOYEE INFORMATION */}
          {viewTab === "personal" && (
            <div className="profile-grid">
              <div className="profile-card">
                <label>Emp ID</label>
                <span>{viewEmpId}</span>
              </div>
              <div className="profile-card">
                <label>Name</label>
                <span>{viewName}</span>
              </div>
              <div className="profile-card">
                <label>Department</label>
                {viewDept === null ? <FieldSkeleton /> : <span>{viewDept}</span>}
              </div>
              <div className="profile-card">
                <label>Designation</label>
                {viewDesig === null ? <FieldSkeleton /> : <span>{viewDesig}</span>}
              </div>
            </div>
          )}

          {/* REGULARIZATION DETAILS */}
          {viewTab === "regularization" && (
            <>
              <div className="profile-grid">
                <div className="profile-card">
                  <label>Date</label>
                  <span>{r.date || "—"}</span>
                </div>
                <div className="profile-card">
                  <label>Status</label>
                  <span className={`leaves-status ${getRegularizationStatusClass(r.status)}`}>
                    {getRegularizationStatusLabel(r.status)}
                  </span>
                </div>
                <div className="profile-card">
                  <label>Proposed Check-In</label>
                  <span>{formatTime12(r.requested_check_in) || "—"}</span>
                </div>
                <div className="profile-card">
                  <label>Proposed Check-Out</label>
                  <span>{formatTime12(r.requested_check_out) || "—"}</span>
                </div>
                <div className="profile-card">
                  <label>Proposed Break</label>
                  <span>{r.requested_break_hours ? `${r.requested_break_hours}h` : "—"}</span>
                </div>
              </div>

              <div className="profile-card" style={{ marginTop: 16, gridColumn: "1 / -1" }}>
                <label>Reason</label>
                <span style={{ fontWeight: 400 }}>{r.reason || "—"}</span>
              </div>

              {r.remarks && (
                <div className="profile-card" style={{ marginTop: 12, gridColumn: "1 / -1" }}>
                  <label>Remarks</label>
                  <span style={{ fontWeight: 400 }}>{r.remarks}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* STICKY FOOTER */}
        <div
          className="employee-page-footer"
          style={{ padding: "22px 0 0", margin: "24px 0 0", justifyContent: "flex-end" }}
        >
          <div className="employee-actions">
            {canManage && r.status === RegularizationStatus.PENDING && (
              <>
                <button
                  className="primary"
                  style={{ background: "#28a745" }}
                  onClick={() => onApprovalAction(r.id, "approve")}
                >
                  Approve
                </button>
                <button
                  className="primary"
                  style={{ background: "#dc3545" }}
                  onClick={() => onApprovalAction(r.id, "reject")}
                >
                  Reject
                </button>
              </>
            )}

            <button className="secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewRegularization;